import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';
import { baseUrl } from '../api/restClient';
import { getPublicCreativeId } from '../helpers/creativeId';
import jmgLogoDark from '../assets/jmg-logo-dark.png';
import jmgLogoLight from '../assets/jmg-logo-light.png';
import { useColorMode } from '../theme';
import { FillRing } from '../helpers/common';
import SupplyInvoiceAcceptanceState from '../features/Invoices/SupplyInvoiceAcceptanceState';
import HR from '../components/common/HR';
import { EditSlider } from '../components/common/EditSlider';
import PDFView from '../components/common/PDFView';
import { getRequiredDeliverablesForFormat } from '../constants/mediaFormats';
import { readUiLanguage, UI_LANGUAGE_OPTIONS, writeUiLanguage } from '../helpers/uiLanguage';
import { CREATOR_PORTAL_LOCALE_BY_LANGUAGE, formatCreatorPortalDateTime, getCreatorPortalText } from '../helpers/creatorPortalI18n';
import { useAlert } from '../context/AlertContext';

const PORTAL_SESSION_STORAGE_PREFIX = 'creator-portal-session:';
const CREATOR_MESSAGE_LIMIT_TOOLTIP = 'Max message limit reached. Please wait and try again or edit your last sent message.';
const getPortalSessionStorageKey = (uuid) => `${PORTAL_SESSION_STORAGE_PREFIX}${String(uuid || '').trim()}`;

const readStoredPortalSession = (uuid) => {
  const key = getPortalSessionStorageKey(uuid);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = String(parsed?.token || '').trim();
    const expiresAt = parsed?.expiresAt ? new Date(parsed.expiresAt) : null;
    if (!token || !expiresAt || Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
      localStorage.removeItem(key);
      return null;
    }
    return { token, expiresAt: expiresAt.toISOString() };
  } catch {
    return null;
  }
};

const writeStoredPortalSession = (uuid, session) => {
  const key = getPortalSessionStorageKey(uuid);
  const token = String(session?.token || '').trim();
  const expiresAt = session?.expiresAt;
  if (!token || !expiresAt) return;
  localStorage.setItem(key, JSON.stringify({ token, expiresAt }));
};

const clearStoredPortalSession = (uuid) => {
  const key = getPortalSessionStorageKey(uuid);
  localStorage.removeItem(key);
};

const clearAllStoredPortalSessions = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PORTAL_SESSION_STORAGE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // no-op
  }
};

const maskEmailLocalPart = (email) => {
  const rawEmail = String(email || '').trim();
  if (!rawEmail) return '';

  const atIndex = rawEmail.indexOf('@');
  if (atIndex <= 0 || atIndex === rawEmail.length - 1) return rawEmail;

  const local = rawEmail.slice(0, atIndex);
  const domain = rawEmail.slice(atIndex + 1);
  const visibleChars = Math.min(4, Math.max(1, local.length - 1));
  const hiddenChars = Math.max(1, local.length - visibleChars);
  const maskedLocal = `${local.slice(0, visibleChars)}${'*'.repeat(hiddenChars)}`;
  return `${maskedLocal}@${domain}`;
};

const formatRemainingCooldown = (ms) => {
  const safeMs = Math.max(0, Number(ms) || 0);
  if (!safeMs) return 'less than 1 minute';

  const totalMinutes = Math.ceil(safeMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
};

const jsonRequest = async (path, options = {}) => {
  const res = await fetch(`${baseUrl}${path}`, {
    method: options.method || 'GET',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    body: options.body ? JSON.stringify(options.body) : undefined
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed with ${res.status}`);
  }

  return res.json();
};

const uploadRequest = async (path, formData, headers = {}) => {
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers,
    body: formData
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Upload failed with ${res.status}`);
  }

  return res.json();
};

const resolveCreativeHref = (creativeType, creativeId) => {
  const id = getPublicCreativeId(creativeId) || '';
  if (!id) return '';
  return creativeType === 'youtube' ? `https://youtube.com/watch?v=${id}` : id;
};

const extractYoutubeId = (creativeId) => {
  const raw = getPublicCreativeId(creativeId) || '';
  if (!raw) return '';
  if (!raw.includes('http')) return raw;
  try {
    const url = new URL(raw);
    if (url.hostname.includes('youtu.be')) {
      return url.pathname.replace('/', '');
    }
    return url.searchParams.get('v') || '';
  } catch {
    return '';
  }
};

const resolveEmbedSrc = (creativeType, creativeId) => {
  const href = resolveCreativeHref(creativeType, creativeId);
  if (!href) return '';

  if (creativeType === 'youtube') {
    const youtubeId = extractYoutubeId(creativeId);
    if (!youtubeId) return '';
    return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&playsinline=1&rel=0`;
  }

  if (creativeType === 'instagram') {
    try {
      const url = new URL(href);
      if (url.hostname.includes('instagram.com')) {
        const cleanPath = url.pathname.replace(/\/+$/, '');
        return `${url.origin}${cleanPath}/embed`;
      }
    } catch {
      return '';
    }
  }

  if (creativeType === 'tiktok') {
    const match = href.match(/\/video\/(\d+)/i);
    if (match?.[1]) {
      return `https://www.tiktok.com/embed/v2/${match[1]}`;
    }
  }

  return '';
};

const getPreviewKind = (file) => {
  const url = String(file?.url || '').toLowerCase();
  const contentType = String(file?.contentType || '').toLowerCase();

  if (!url) return 'none';
  if (url.includes('drive.google.com')) return 'drive';
  if (contentType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url)) return 'image';
  if (contentType.includes('pdf') || /\.pdf(\?|$)/i.test(url)) return 'pdf';
  if (contentType.startsWith('video/') || /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) return 'video';
  return 'iframe';
};

const getPreviewKindByUrl = (url, contentType) => getPreviewKind({ url, contentType });

const resolveDeliverableFrameSrc = (url) => {
  const raw = String(url || '').trim();
  if (!raw) return '';

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();

    if (host.includes('youtube.com') || host.includes('youtu.be')) {
      let videoId = '';
      if (host.includes('youtu.be')) {
        videoId = parsed.pathname.replace('/', '').split('/')[0];
      } else if (parsed.pathname.startsWith('/shorts/')) {
        videoId = parsed.pathname.replace('/shorts/', '').split('/')[0];
      } else {
        videoId = parsed.searchParams.get('v') || '';
      }
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playsinline=1&rel=0`;
      }
    }
  } catch {
    return raw;
  }

  return raw;
};

const extractDriveFileId = (url) => {
  const raw = String(url || '').trim();
  if (!raw.includes('drive.google.com')) return '';
  const byPath = raw.match(/\/file\/d\/([^/]+)/i)?.[1];
  if (byPath) return byPath;
  try {
    const parsed = new URL(raw);
    return parsed.searchParams.get('id') || '';
  } catch {
    return '';
  }
};

const resolveDirectMediaSrc = (url) => {
  const raw = String(url || '').trim();
  const driveFileId = extractDriveFileId(raw);
  if (driveFileId) {
    return `https://drive.google.com/uc?export=view&id=${driveFileId}`;
  }
  return raw;
};

const resolveDrivePreviewSrc = (url) => {
  const fileId = extractDriveFileId(url);
  if (!fileId) return String(url || '').trim();
  return `https://drive.google.com/file/d/${fileId}/preview`;
};

const DriveBestEffortPreview = ({ url, compact = false, text = {} }) => {
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setTimedOut(false);
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, [url]);

  if (!url) return null;

  if (timedOut && !loaded) {
    return (
      <Box sx={{ p: compact ? 1 : 2, width: compact ? 320 : 'auto' }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
          {text.drivePreviewFallback || 'Preview could not be loaded. Open the file directly.'}
          </Typography>
        <Link href={url} target="_blank" rel="noreferrer">{text.openFile || 'Open file'}</Link>
      </Box>
    );
  }

  return (
    <Box sx={{ width: compact ? 340 : '100%' }}>
      <Box
        component="iframe"
        src={resolveDrivePreviewSrc(url)}
        title="Drive file preview"
        onLoad={() => setLoaded(true)}
        sx={{
          width: compact ? 340 : '100%',
          height: compact ? 220 : '85vh',
          border: 0,
          display: 'block'
        }}
      />
      {!compact ? (
        <Box sx={{ pt: 1 }}>
          <Button
            variant="text"
            component="a"
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            {text.openFile || 'Open file'}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

const DeliverablePreview = ({ file, text = {} }) => {
  const kind = getPreviewKind(file);
  const url = file?.url || '';
  const [imageFailed, setImageFailed] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  if (!url) return <Typography variant="caption">{text.previewNotAvailable || 'Preview is not available.'}</Typography>;

  if (kind === 'image') {
    if (imageFailed) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            {text.imagePreviewNotAvailable || 'Image preview is not available.'}
          </Typography>
          <Link href={url} target="_blank" rel="noreferrer">{text.openFile || 'Open file'}</Link>
        </Box>
      );
    }
    return (
      <Box
        component="img"
        src={resolveDirectMediaSrc(url)}
        alt={file?.name || 'Deliverable preview'}
        onError={() => setImageFailed(true)}
        sx={{ width: 320, maxHeight: 220, objectFit: 'contain', display: 'block' }}
      />
    );
  }

  if (kind === 'video') {
    return (
      <Box
        component="video"
        src={resolveDirectMediaSrc(url)}
        controls
        muted
        playsInline
        sx={{ width: 320, maxHeight: 220, display: 'block' }}
      />
    );
  }

  if (kind === 'drive') return <DriveBestEffortPreview url={url} compact text={text} />;

  const frameSrc =
    resolveDeliverableFrameSrc(url);

  if (previewFailed) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          {text.previewBlocked || 'Preview is blocked for this file.'}
        </Typography>
        <Link href={url} target="_blank" rel="noreferrer">{text.openFile || 'Open file'}</Link>
      </Box>
    );
  }

  return (
    <Box
      component="iframe"
      src={frameSrc}
      title={file?.name || 'Deliverable preview'}
      onError={() => setPreviewFailed(true)}
      sx={{ width: 340, height: 220, border: 0, display: 'block' }}
      allow="autoplay"
    />
  );
};

const groupConsecutiveActivities = (entries = []) => {
  const groups = [];

  entries.forEach((entry) => {
    const what = entry?.what || 'Activity';
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.what === what) {
      lastGroup.items.push(entry);
      return;
    }

    groups.push({
      id: `${what}-${entry?.when || ''}-${groups.length}`,
      what,
      items: [entry]
    });
  });

  return groups;
};

const hasDeliverableValue = (item) => !!String(item?.url || item?.value || '').trim();

const DiscordLogoIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M20.32 4.37a19.79 19.79 0 0 0-4.95-1.56.08.08 0 0 0-.08.04 13.7 13.7 0 0 0-.61 1.24 18.27 18.27 0 0 0-5.36 0 12.6 12.6 0 0 0-.62-1.24.08.08 0 0 0-.08-.04 19.74 19.74 0 0 0-4.95 1.56.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06a.08.08 0 0 0 .03.06 19.9 19.9 0 0 0 6.07 3.07.08.08 0 0 0 .08-.03c.47-.65.9-1.34 1.27-2.06a.08.08 0 0 0-.04-.11 13.2 13.2 0 0 1-1.94-.93.08.08 0 0 1-.01-.13c.13-.1.26-.2.38-.31a.08.08 0 0 1 .08-.01c4.06 1.85 8.45 1.85 12.46 0a.08.08 0 0 1 .09.01c.12.11.25.21.38.31a.08.08 0 0 1-.01.13 12.7 12.7 0 0 1-1.94.93.08.08 0 0 0-.04.11c.38.72.8 1.41 1.27 2.06a.08.08 0 0 0 .08.03 19.86 19.86 0 0 0 6.07-3.07.08.08 0 0 0 .03-.06c.5-5.18-.84-9.67-3.55-13.66a.06.06 0 0 0-.03-.03ZM8.02 15.33c-1.22 0-2.23-1.13-2.23-2.52s.98-2.52 2.23-2.52c1.26 0 2.25 1.14 2.23 2.52 0 1.39-.99 2.52-2.23 2.52Zm7.97 0c-1.22 0-2.23-1.13-2.23-2.52s.98-2.52 2.23-2.52c1.26 0 2.25 1.14 2.23 2.52 0 1.39-.98 2.52-2.23 2.52Z"
    />
  </SvgIcon>
);

const resolveActivationProductImage = (activation = {}) => {
  const campaignProduct = activation?.campaign?.product || {};
  return String(
    campaignProduct?.image ||
    campaignProduct?.productImage ||
    campaignProduct?.logoUrl ||
    campaignProduct?.logoURL ||
    ''
  ).trim();
};

const formatActivationEarnings = (activation = {}, language = 'EN') => {
  const rawAmount = activation?.earnings?.amount;
  const amount = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount);
  if (!Number.isFinite(amount)) return '-';

  const currency = String(activation?.earnings?.currency || 'USD').trim().toUpperCase() === 'EUR'
    ? 'EUR'
    : 'USD';
  const locale = CREATOR_PORTAL_LOCALE_BY_LANGUAGE?.[language] || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const normalizeIntegrationTimestamp = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const onlyDigits = raw.replace(/\D/g, '');
  if (!onlyDigits) return '';

  if (raw.includes(':')) {
    const parts = raw.split(':').map((p) => p.replace(/\D/g, '')).filter(Boolean);
    if (parts.length === 2) {
      const mm = parts[0].padStart(2, '0');
      const ss = parts[1].padStart(2, '0');
      return `${mm}:${ss}`;
    }
    if (parts.length >= 3) {
      const hh = String(Number(parts[0] || 0));
      const mm = String(Number(parts[1] || 0)).padStart(2, '0');
      const ss = String(Number(parts[2] || 0)).padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    }
  }

  if (onlyDigits.length <= 2) return `00:${onlyDigits.padStart(2, '0')}`;
  if (onlyDigits.length <= 4) {
    const mm = onlyDigits.slice(0, -2).padStart(2, '0');
    const ss = onlyDigits.slice(-2);
    return `${mm}:${ss}`;
  }

  const hh = String(Number(onlyDigits.slice(0, -4)));
  const mm = onlyDigits.slice(-4, -2);
  const ss = onlyDigits.slice(-2);
  return `${hh}:${mm}:${ss}`;
};

const isValidIntegrationTimestamp = (value) => {
  const v = String(value || '').trim();
  if (!v) return false;
  const mmss = /^(\d{1,2}):([0-5]\d)$/;
  const hhmmss = /^(\d{1,2}):([0-5]\d):([0-5]\d)$/;
  return mmss.test(v) || hhmmss.test(v);
};

const CreatorPortal = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const { modeSetting, setModeSetting } = useColorMode();
  const { notify } = useAlert();
  const [language, setLanguage] = useState(() => readUiLanguage());
  const { uuid: routeUuid } = useParams();
  const [loginUuid, setLoginUuid] = useState(routeUuid || '');
  const [loginStep, setLoginStep] = useState('uuid');
  const [challengeId, setChallengeId] = useState('');
  const [challengeDestination, setChallengeDestination] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [portalSessionToken, setPortalSessionToken] = useState('');
  const [discordPendingLinkToken, setDiscordPendingLinkToken] = useState('');
  const [authErrorMessage, setAuthErrorMessage] = useState('');
  const [portalData, setPortalData] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [discordActionLoading, setDiscordActionLoading] = useState(false);
  const [activationSwitching, setActivationSwitching] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [itFeedbackOpen, setItFeedbackOpen] = useState(false);
  const [itFeedbackMessage, setItFeedbackMessage] = useState('');
  const [itFeedbackSubmitting, setItFeedbackSubmitting] = useState(false);
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [emailPromptValue, setEmailPromptValue] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState('');
  const [messagesRefreshing, setMessagesRefreshing] = useState(false);
  const [activeRequirementAction, setActiveRequirementAction] = useState('');
  const [deliverableDrafts, setDeliverableDrafts] = useState({});
  const [expandedActivityGroups, setExpandedActivityGroups] = useState({});
  const [openAttachmentPreview, setOpenAttachmentPreview] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const otpRefs = useRef([]);
  const isActivationsPage = useMemo(
    () => String(location?.pathname || '').endsWith('/activations'),
    [location?.pathname]
  );

  const activeUuid = useMemo(() => portalData?.uuid || loginUuid, [portalData?.uuid, loginUuid]);
  const activityGroups = useMemo(() => {
    const entries = (portalData?.portal?.pageHistory || []).slice().reverse().slice(0, 80);
    return groupConsecutiveActivities(entries);
  }, [portalData?.portal?.pageHistory]);
  const requiredDeliverables = useMemo(() => {
    const fromApi = portalData?.portal?.requiredDeliverables;
    if (Array.isArray(fromApi) && fromApi.length) return fromApi;

    const fallback = getRequiredDeliverablesForFormat(portalData?.format).requirements;
    return fallback.map((item) => ({ ...item, completed: false, uploadsCount: 0, latest: null }));
  }, [portalData?.portal?.requiredDeliverables, portalData?.format]);
  const deliverablesByRequirement = useMemo(() => {
    const map = {};
    (portalData?.portal?.deliverables || []).forEach((item) => {
      if (!item?.requirementKey || !hasDeliverableValue(item)) return;
      if (!map[item.requirementKey]) map[item.requirementKey] = [];
      map[item.requirementKey].push(item);
    });
    return map;
  }, [portalData?.portal?.deliverables]);
  const activationItems = useMemo(() => {
    return Array.isArray(portalData?.portal?.creatives) ? portalData.portal.creatives : [];
  }, [portalData?.portal?.creatives]);
  const isDiscordConnected = useMemo(
    () => !!portalData?.portal?.discord?.connected,
    [portalData?.portal?.discord?.connected]
  );
  const isDiscordIdentitySession = useMemo(() => {
    const identityKey = String(portalData?.portal?.scope?.identityKey || '').trim().toLowerCase();
    return identityKey.startsWith('discord:');
  }, [portalData?.portal?.scope?.identityKey]);
  const discordAvatarUrl = useMemo(
    () => String(portalData?.portal?.discord?.avatarUrl || '').trim(),
    [portalData?.portal?.discord?.avatarUrl]
  );
  const channelThumbnailUrl = useMemo(
    () => String(portalData?.channel?.thumbnail || '').trim(),
    [portalData?.channel?.thumbnail]
  );
  const portalUserAvatarSrc = useMemo(() => {
    if (isDiscordIdentitySession && discordAvatarUrl) return discordAvatarUrl;
    if (channelThumbnailUrl) return channelThumbnailUrl;
    return discordAvatarUrl;
  }, [isDiscordIdentitySession, discordAvatarUrl, channelThumbnailUrl]);
  const channelAvatarSrc = useMemo(() => {
    if (channelThumbnailUrl) return channelThumbnailUrl;
    return discordAvatarUrl;
  }, [channelThumbnailUrl, discordAvatarUrl]);
  const portalUserName = useMemo(() => {
    return (
      portalData?.portal?.discord?.discordGlobalName ||
      portalData?.portal?.discord?.discordUsername ||
      portalData?.channel?.name ||
      ''
    );
  }, [portalData?.portal?.discord?.discordGlobalName, portalData?.portal?.discord?.discordUsername, portalData?.channel?.name]);
  const portalUserInitial = useMemo(() => String(portalUserName || 'U').trim().charAt(0).toUpperCase() || 'U', [portalUserName]);
  const portalMessages = useMemo(() => (
    Array.isArray(portalData?.portal?.comments) ? portalData.portal.comments : []
  ), [portalData?.portal?.comments]);
  const lastCreatorMessage = useMemo(() => {
    for (let idx = portalMessages.length - 1; idx >= 0; idx -= 1) {
      const entry = portalMessages[idx];
      if (String(entry?.author || '').toLowerCase() === 'creator') return entry;
    }
    return null;
  }, [portalMessages]);
  const canSendNewMessageFromServer = portalData?.portal?.messaging?.canSendNewMessage !== false;
  const messageCooldownEndsAtMs = useMemo(() => {
    const rawValue = portalData?.portal?.messaging?.cooldownEndsAt;
    if (!rawValue) return null;
    const date = new Date(rawValue);
    const time = date.getTime();
    return Number.isNaN(time) ? null : time;
  }, [portalData?.portal?.messaging?.cooldownEndsAt]);
  const [messageCooldownNowMs, setMessageCooldownNowMs] = useState(() => Date.now());
  const isLocallyPastCooldown = !!(messageCooldownEndsAtMs && messageCooldownNowMs >= messageCooldownEndsAtMs);
  const canSendNewMessage = canSendNewMessageFromServer || isLocallyPastCooldown;
  const canEditLastCreatorMessage = !!lastCreatorMessage && (portalData?.portal?.messaging?.canEditLastCreatorMessage !== false);
  const isEditingMessage = !!editingCommentId;
  const isNewMessageBlocked = !isEditingMessage && !canSendNewMessage;
  const remainingMessageCooldownMs = isNewMessageBlocked && messageCooldownEndsAtMs
    ? Math.max(0, messageCooldownEndsAtMs - messageCooldownNowMs)
    : 0;
  const messageBlockedTooltip = isNewMessageBlocked
    ? messageCooldownEndsAtMs
      ? `${CREATOR_MESSAGE_LIMIT_TOOLTIP} Remaining time: ${formatRemainingCooldown(remainingMessageCooldownMs)}.`
      : CREATOR_MESSAGE_LIMIT_TOOLTIP
    : '';
  const sendButtonDisabled = commentSubmitting || !String(comment || '').trim() || isNewMessageBlocked;

  const resetMessages = () => {};
  const notifyError = (message, duration = 5000) => {
    if (!message) return;
    notify({ severity: 'error', message, duration });
  };
  const notifySuccess = (message, duration = 5000) => {
    if (!message) return;
    notify({ severity: 'success', message, duration });
  };

  const resolvePortalSessionToken = (uuid) => {
    if (portalSessionToken) return portalSessionToken;
    return readStoredPortalSession(uuid)?.token || '';
  };

  const completeDiscordPendingLink = async ({ uuid, sessionToken }) => {
    const safeUuid = String(uuid || '').trim();
    const safeSessionToken = String(sessionToken || '').trim();
    const pendingLinkToken = String(discordPendingLinkToken || '').trim();
    if (!safeUuid || !safeSessionToken || !pendingLinkToken) {
      throw new Error('Missing Discord linking context');
    }

    const data = await jsonRequest('/creative/portal/discord/link/complete', {
      method: 'POST',
      body: {
        uuid: safeUuid,
        portalSessionToken: safeSessionToken,
        pendingLinkToken,
      },
      headers: {
        'x-portal-session': safeSessionToken,
        'x-portal-uuid': safeUuid,
      },
    });
    return data;
  };

  const portalSessionHeaders = (uuid) => {
    const token = resolvePortalSessionToken(uuid);
    return token
      ? {
          'x-portal-session': token,
          'x-portal-uuid': String(uuid || '').trim(),
        }
      : {};
  };

  const handlePortalSessionError = (uuid, err) => {
    const message = String(err?.message || '');
    if (!message.toLowerCase().includes('invalid portal session') && !message.toLowerCase().includes('missing portal session token')) {
      return false;
    }
    clearStoredPortalSession(uuid);
    setPortalSessionToken('');
    setPortalData(null);
    setLoginStep('uuid');
    notifyError(t('sessionExpired'));
    return true;
  };

  const tryRestoreSession = async (uuid) => {
    const stored = readStoredPortalSession(uuid);
    if (!stored) return null;
    try {
      const data = await jsonRequest('/creative/portal/session/restore', {
        method: 'POST',
        body: {
          uuid,
          sessionToken: stored.token
        }
      });
      return { data, token: stored.token };
    } catch {
      clearStoredPortalSession(uuid);
      return null;
    }
  };

  const handleLogin = async (uuidToUse, emailOverride = '') => {
    const uuid = String(uuidToUse || loginUuid || '').trim();
    const loginEmail = String(emailOverride || '').trim();
    if (!uuid) {
      notifyError(t('enterValueFor', { label: t('myId') }));
      return;
    }

    resetMessages();
    setLoading(true);
    try {
      if (!discordPendingLinkToken) {
        const restored = await tryRestoreSession(uuid);
        if (restored?.data) {
          setPortalData(restored.data);
          setPortalSessionToken(restored.token || '');
          setLoginUuid(uuid);
          notifySuccess(t('signedInWithActiveSession'));
          return;
        }
      }

      const data = await jsonRequest('/creative/portal/login', {
        method: 'POST',
        body: {
          uuid,
          ...(loginEmail ? { email: loginEmail } : {})
        }
      });
      if (data?.requiresEmail) {
        setEmailPromptValue(loginEmail);
        setEmailPromptOpen(true);
        setLoginUuid(uuid);
        return;
      }
      if (data?.requiresVerification) {
        setChallengeId(data.challengeId || '');
        setChallengeDestination(data.destination || '');
        setOtpDigits(['', '', '', '', '', '']);
        setLoginStep('verify');
        notifySuccess(t('codeEmailSentToX', { email: maskEmailLocalPart(data?.email || data?.destination || '') }));
      } else {
        setPortalData(data);
      }
      setLoginUuid(uuid);
      setEmailPromptOpen(false);
      setEmailPromptValue('');
    } catch (err) {
      if (emailPromptOpen || loginEmail) {
        notifyError(t('loginFailed', { message: err.message }));
        return;
      }
      setPortalData(null);
      setAuthErrorMessage(t('loginFailed', { message: err.message }));
      setLoginStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendFallbackEmail = () => {
    const email = String(emailPromptValue || '').trim();
    if (!email) {
      notifyError(t('enterValueFor', { label: t('emailAddress') }));
      return;
    }
    handleLogin(loginUuid, email);
  };

  const handleVerifyCode = async () => {
    const uuid = String(loginUuid || '').trim();
    const code = otpDigits.join('');
    if (!uuid || !challengeId || code.length !== 6) {
      notifyError(t('missingVerificationCode'));
      return;
    }

    resetMessages();
    setLoading(true);
    try {
      const data = await jsonRequest('/creative/portal/login/verify', {
        method: 'POST',
        body: {
          uuid,
          challengeId,
          code
        }
      });
      if (data?.portalSession?.token && data?.portalSession?.expiresAt) {
        writeStoredPortalSession(uuid, data.portalSession);
        setPortalSessionToken(data.portalSession.token);
      }

      if (discordPendingLinkToken) {
        const linkPayload = await completeDiscordPendingLink({
          uuid,
          sessionToken: data?.portalSession?.token || '',
        });
        applyPortalPayload(linkPayload);
        setDiscordPendingLinkToken('');
        setOtpDigits(['', '', '', '', '', '']);
        setChallengeId('');
        setChallengeDestination('');
        notifySuccess('Discord account linked.');
        return;
      }

      setPortalData(data);
      setLoginStep('uuid');
      setOtpDigits(['', '', '', '', '', '']);
      setChallengeId('');
      setChallengeDestination('');
    } catch (err) {
      setPortalData(null);
      setAuthErrorMessage(t('verificationFailed', { message: err.message }));
      setLoginStep('error');
    } finally {
      setLoading(false);
    }
  };

  const applyPortalPayload = (payload) => {
    const nextPortalData = payload?.portalData || payload;
    const nextUuid = String(nextPortalData?.uuid || '').trim();
    if (payload?.portalSession?.token && payload?.portalSession?.expiresAt && nextUuid) {
      writeStoredPortalSession(nextUuid, payload.portalSession);
      setPortalSessionToken(payload.portalSession.token);
    }
    if (nextPortalData) {
      setPortalData(nextPortalData);
    }
    if (nextUuid) {
      setLoginUuid(nextUuid);
    }
    setLoginStep('uuid');
    setAuthErrorMessage('');
  };

  const handleDiscordAuth = (mode = 'login') => {
    const normalizedMode = String(mode || '').toLowerCase();
    const isConnectMode = normalizedMode === 'connect';
    const uuid = String(activeUuid || '').trim();
    const token = isConnectMode ? resolvePortalSessionToken(uuid) : '';

    if (isConnectMode && !uuid) {
      notifyError(t('enterValueFor', { label: t('myId') }));
      return;
    }
    if (isConnectMode && !token) {
      notifyError(t('sessionExpired'));
      return;
    }

    setDiscordActionLoading(true);
    jsonRequest('/creative/portal/discord/url', {
      method: 'POST',
      body: {
        mode: normalizedMode || 'login',
        ...(isConnectMode ? { uuid, portalSessionToken: token } : {}),
      },
    })
      .then((result) => {
        const authorizeUrl = String(result?.authorizeUrl || '').trim();
        if (!authorizeUrl) {
          throw new Error('Discord authorize URL is missing');
        }

        const popup = window.open(
          authorizeUrl,
          'creator-portal-discord',
          'width=540,height=760,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no'
        );

        if (!popup) {
          setDiscordActionLoading(false);
          notifyError(normalizedMode === 'login'
            ? t('discordLoginFailed', { message: 'Popup blocked' })
            : t('discordConnectFailed', { message: 'Popup blocked' }));
          return;
        }

        let finished = false;
        let closeDetected = false;
        let pollTimer = null;
        let timeoutTimer = null;
        let closeGraceTimer = null;
        let onMessage = () => {};
        const finish = () => {
          window.removeEventListener('message', onMessage);
          if (pollTimer) window.clearInterval(pollTimer);
          if (timeoutTimer) window.clearTimeout(timeoutTimer);
          if (closeGraceTimer) window.clearTimeout(closeGraceTimer);
          setDiscordActionLoading(false);
        };

        onMessage = (event) => {
          if (event?.data?.source !== 'creator-portal-discord') return;
          finished = true;
          finish();

          const payload = event?.data?.payload || {};
          if (payload?.mode === 'link_required' && payload?.pendingLinkToken) {
            setDiscordPendingLinkToken(String(payload.pendingLinkToken || ''));
            setPortalData(null);
            setPortalSessionToken('');
            setLoginStep('uuid');
            setAuthErrorMessage('');
            setChallengeId('');
            setChallengeDestination('');
            setOtpDigits(['', '', '', '', '', '']);
            notifySuccess('Discord verified. Enter your My ID to link your account.');
            return;
          }
          if (!payload?.ok) {
            notifyError(normalizedMode === 'login'
              ? t('discordLoginFailed', { message: payload?.error || '' })
              : t('discordConnectFailed', { message: payload?.error || '' }));
            return;
          }

          applyPortalPayload(payload);
          setDiscordPendingLinkToken('');

          if (payload?.mode === 'connect') {
            notifySuccess(t('discordConnectSuccess'));
          } else {
            notifySuccess(t('signedInWithActiveSession'));
          }
        };

        pollTimer = window.setInterval(() => {
          if (popup.closed) {
            if (!closeDetected) {
              closeDetected = true;
              closeGraceTimer = window.setTimeout(() => {
                if (finished) return;
                finish();
                notifyError(normalizedMode === 'login'
                  ? t('discordLoginFailed', { message: 'No callback response received' })
                  : t('discordConnectFailed', { message: 'No callback response received' }));
              }, 900);
            }
          }
        }, 350);

        timeoutTimer = window.setTimeout(() => {
          if (finished) return;
          try {
            popup.close();
          } catch {
            // no-op
          }
          finish();
          notifyError(normalizedMode === 'login'
            ? t('discordLoginFailed', { message: 'Timeout' })
            : t('discordConnectFailed', { message: 'Timeout' }));
        }, 180000);

        window.addEventListener('message', onMessage);
      })
      .catch((err) => {
        setDiscordActionLoading(false);
        notifyError(normalizedMode === 'login'
          ? t('discordLoginFailed', { message: err.message })
          : t('discordConnectFailed', { message: err.message }));
      });
  };

  const handleSelectActivation = async (targetUuid, options = {}) => {
    const navigateToDetails = options?.navigateToDetails === true;
    const silent = options?.silent === true;
    const selectedUuid = String(targetUuid || '').trim();
    const currentUuid = String(activeUuid || '').trim();
    if (!selectedUuid || !currentUuid) return false;
    if (selectedUuid === currentUuid) {
      if (navigateToDetails) {
        navigate(`/creator-portal/${selectedUuid}`);
      }
      return true;
    }

    const token = resolvePortalSessionToken(currentUuid);
    if (!token) {
      if (!silent) notifyError(t('sessionExpired'));
      return false;
    }

    setActivationSwitching(true);
    try {
      const data = await jsonRequest('/creative/portal/session/select', {
        method: 'POST',
        body: {
          uuid: currentUuid,
          targetUuid: selectedUuid,
          portalSessionToken: token,
        },
        headers: portalSessionHeaders(currentUuid),
      });
      if (data?.portalSession?.token && data?.portalSession?.expiresAt) {
        writeStoredPortalSession(selectedUuid, data.portalSession);
        setPortalSessionToken(data.portalSession.token);
      }
      setPortalData(data);
      setLoginUuid(selectedUuid);
      if (navigateToDetails) {
        navigate(`/creator-portal/${selectedUuid}`);
      }
      return true;
    } catch (err) {
      if (handlePortalSessionError(currentUuid, err)) return false;
      if (!silent) notifyError(t('activationSwitchFailed', { message: err.message }));
      return false;
    } finally {
      setActivationSwitching(false);
    }
  };

  const focusOtpIndex = (index) => {
    const input = otpRefs.current[index];
    if (input) input.focus();
  };

  const handleOtpChange = (index, value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) {
      setOtpDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }

    setOtpDigits((prev) => {
      const next = [...prev];
      if (digits.length === 1) {
        next[index] = digits;
        return next;
      }
      const merged = [...next];
      let cursor = index;
      for (const ch of digits.slice(0, 6 - index)) {
        merged[cursor] = ch;
        cursor += 1;
      }
      return merged;
    });

    if (digits.length === 1 && index < 5) {
      focusOtpIndex(index + 1);
    } else if (digits.length > 1) {
      focusOtpIndex(Math.min(index + digits.length, 5));
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      focusOtpIndex(index - 1);
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusOtpIndex(index - 1);
    }
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      focusOtpIndex(index + 1);
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasted = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = ['','','','','',''];
    pasted.split('').forEach((ch, idx) => {
      next[idx] = ch;
    });
    setOtpDigits(next);
    focusOtpIndex(Math.min(pasted.length, 6) - 1);
  };

  const handleBackToLogin = () => {
    setPortalData(null);
    setLoginStep('uuid');
    setChallengeId('');
    setChallengeDestination('');
    setOtpDigits(['', '', '', '', '', '']);
    setPortalSessionToken('');
    setDiscordPendingLinkToken('');
    setAuthErrorMessage('');
    setComment('');
    setActiveRequirementAction('');
    setDeliverableDrafts({});
    setExpandedActivityGroups({});
    setOpenAttachmentPreview(null);
    setDiscordActionLoading(false);
    setActivationSwitching(false);
    setLogoutLoading(false);
  };

  const handleOpenAttachmentPreview = (event, file) => {
    event.preventDefault();
    if (!file?.url) return;
    setOpenAttachmentPreview(file);
  };

  const toggleActivityGroup = (groupId) => {
    setExpandedActivityGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const cycleThemeMode = () => {
    const order = ['system', 'light', 'dark'];
    const currentIdx = order.indexOf(modeSetting || 'system');
    const nextMode = order[(currentIdx + 1) % order.length];
    setModeSetting(nextMode);
  };

  const themeIcon = modeSetting === 'light'
    ? <LightModeIcon />
    : modeSetting === 'dark'
      ? <DarkModeIcon />
      : <SettingsBrightnessIcon />;

  const themeLabel = modeSetting === 'light'
    ? (getCreatorPortalText(language).themeLight || 'Theme: light')
    : modeSetting === 'dark'
      ? (getCreatorPortalText(language).themeDark || 'Theme: dark')
      : (getCreatorPortalText(language).themeSystem || 'Theme: system');
  const handleLanguageChange = (nextLanguage) => {
    writeUiLanguage(null, nextLanguage);
    setLanguage(nextLanguage);
  };
  const handleOpenLanguageMenu = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };
  const handleCloseLanguageMenu = () => {
    setLanguageMenuAnchor(null);
  };
  const handleSelectLanguage = (nextLanguage) => {
    handleLanguageChange(nextLanguage);
    handleCloseLanguageMenu();
  };
  const cycleLanguage = () => {
    const currentIndex = UI_LANGUAGE_OPTIONS.indexOf(language);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % UI_LANGUAGE_OPTIONS.length : 0;
    handleLanguageChange(UI_LANGUAGE_OPTIONS[nextIndex]);
  };
  const handleOpenUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };
  const handleOpenItFeedbackModal = () => {
    handleCloseUserMenu();
    setItFeedbackOpen(true);
  };
  const handleCloseItFeedbackModal = () => {
    if (itFeedbackSubmitting) return;
    setItFeedbackOpen(false);
    setItFeedbackMessage('');
  };
  const handleOpenActivationsPage = () => {
    const uuid = String(activeUuid || '').trim();
    handleCloseUserMenu();
    if (!uuid) return;
    navigate(`/creator-portal/${uuid}/activations`);
  };
  const handleConnectDiscordFromMenu = () => {
    handleCloseUserMenu();
    handleDiscordAuth('connect');
  };
  const handleOpenActivationDetailPage = () => {
    const uuid = String(activeUuid || '').trim();
    handleCloseUserMenu();
    if (!uuid) return;
    navigate(`/creator-portal/${uuid}`);
  };
  const handleOpenActivationCard = async (targetUuid) => {
    const switched = await handleSelectActivation(targetUuid, {
      navigateToDetails: true,
      silent: false,
    });
    if (!switched) return;
  };
  const handleBackToLoginFromMenu = () => {
    handleCloseUserMenu();
    navigate('/creator-portal');
    handleBackToLogin();
  };
  const handleLogoutFromMenu = async () => {
    handleCloseUserMenu();

    const uuid = String(activeUuid || '').trim();
    const token = uuid ? resolvePortalSessionToken(uuid) : '';

    setLogoutLoading(true);
    try {
      if (uuid && token) {
        await jsonRequest('/creative/portal/session/logout', {
          method: 'POST',
          body: {
            uuid,
            portalSessionToken: token,
          },
          headers: portalSessionHeaders(uuid),
        });
      }
    } catch (err) {
      notifyError(t('logoutFailed', { message: err.message }));
    } finally {
      clearAllStoredPortalSessions();
      setLoginUuid('');
      setPortalSessionToken('');
      handleBackToLogin();
      setLogoutLoading(false);
      navigate('/creator-portal');
      notifySuccess(t('loggedOut'));
    }
  };
  const handleSubmitItFeedback = async () => {
    const uuid = String(activeUuid || '').trim();
    const message = String(itFeedbackMessage || '').trim();
    if (!uuid || !message) return;

    const token = resolvePortalSessionToken(uuid);
    if (!token) {
      notifyError(t('sessionExpired'));
      return;
    }

    setItFeedbackSubmitting(true);
    try {
      await jsonRequest('/creative/portal/it-feedback', {
        method: 'POST',
        body: {
          uuid,
          message,
          portalSessionToken: token,
        },
        headers: portalSessionHeaders(uuid),
      });
      notifySuccess('Message sent to IT.');
      setItFeedbackOpen(false);
      setItFeedbackMessage('');
    } catch (err) {
      if (handlePortalSessionError(uuid, err)) return;
      notifyError(err?.message || 'Could not send message to IT');
    } finally {
      setItFeedbackSubmitting(false);
    }
  };
  const i18n = useMemo(() => getCreatorPortalText(language), [language]);
  const t = (key, params) => {
    const value = i18n?.[key];
    if (typeof value === 'function') return value(params || {});
    return value ?? key;
  };

  useEffect(() => {
    if (routeUuid) {
      setLoginUuid(routeUuid);
    }
  }, [routeUuid]);

  useEffect(() => {
    const syncLanguage = () => setLanguage(readUiLanguage());
    window.addEventListener('storage', syncLanguage);
    window.addEventListener('jmg-ui-language-changed', syncLanguage);
    return () => {
      window.removeEventListener('storage', syncLanguage);
      window.removeEventListener('jmg-ui-language-changed', syncLanguage);
    };
  }, []);

  useEffect(() => {
    if (loginStep !== 'verify') return;
    const timer = setTimeout(() => focusOtpIndex(0), 0);
    return () => clearTimeout(timer);
  }, [loginStep]);

  useEffect(() => {
    if (modeSetting !== 'system') {
      setModeSetting('system');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editingCommentId) return;
    const stillExists = portalMessages.some((entry) => String(entry?.id || '') === String(editingCommentId));
    if (!stillExists) {
      setEditingCommentId('');
      setComment('');
    }
  }, [editingCommentId, portalMessages]);

  useEffect(() => {
    if (!messageCooldownEndsAtMs) return undefined;
    if (canSendNewMessageFromServer) return undefined;

    setMessageCooldownNowMs(Date.now());
    const timer = setInterval(() => setMessageCooldownNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(timer);
  }, [messageCooldownEndsAtMs, canSendNewMessageFromServer]);

  const handleCommentSubmit = async () => {
    if (!activeUuid || !comment.trim()) return;
    const message = comment.trim();
    if (!editingCommentId && !canSendNewMessage) return;
    resetMessages();
    setCommentSubmitting(true);
    try {
      const sessionToken = resolvePortalSessionToken(activeUuid);
      const data = await jsonRequest('/creative/portal/comment', {
        method: 'POST',
        body: {
          uuid: activeUuid,
          topic: 'general',
          message,
          portalSessionToken: sessionToken,
          responseMode: 'light',
          editCommentId: editingCommentId || undefined,
        },
        headers: portalSessionHeaders(activeUuid)
      });
      setPortalData((prev) => {
        if (!prev) return prev;
        const nextPortal = { ...(prev.portal || {}) };
        const prevComments = Array.isArray(nextPortal.comments) ? [...nextPortal.comments] : [];
        const prevActivity = Array.isArray(nextPortal.pageHistory) ? [...nextPortal.pageHistory] : [];

        if (data?.comment) {
          if (editingCommentId) {
            nextPortal.comments = prevComments.map((entry) => (
              String(entry?.id || '') === String(editingCommentId)
                ? { ...entry, ...data.comment }
                : entry
            ));
          } else {
            nextPortal.comments = [...prevComments, data.comment];
          }
        }

        if (data?.activity) {
          nextPortal.pageHistory = [...prevActivity, data.activity];
        }
        if (data?.messaging) nextPortal.messaging = data.messaging;

        return { ...prev, portal: nextPortal };
      });
      setComment('');
      setEditingCommentId('');
      notifySuccess(t('commentSent'));
    } catch (err) {
      if (handlePortalSessionError(activeUuid, err)) return;
      notifyError(t('commentSendFailed', { message: err.message }));
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleStartEditLastMessage = () => {
    if (!lastCreatorMessage) return;
    setEditingCommentId(String(lastCreatorMessage.id || ''));
    setComment(String(lastCreatorMessage.message || ''));
  };

  const handleCancelEditLastMessage = () => {
    setEditingCommentId('');
    setComment('');
  };

  const handleRefreshMessages = async () => {
    const uuid = String(activeUuid || '').trim();
    if (!uuid) return;
    const token = resolvePortalSessionToken(uuid);
    if (!token) {
      notifyError(t('sessionExpired'));
      return;
    }

    setMessagesRefreshing(true);
    try {
      const data = await jsonRequest(`/creative/portal/${encodeURIComponent(uuid)}`, {
        method: 'GET',
        headers: portalSessionHeaders(uuid),
      });
      setPortalData(data);
    } catch (err) {
      if (handlePortalSessionError(uuid, err)) return;
      notifyError(err?.message || 'Could not refresh messages');
    } finally {
      setMessagesRefreshing(false);
    }
  };

  const handleFileUpload = async (event, type, requirementKey = '') => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !activeUuid) return;

    resetMessages();
    setActionLoading(true);
    setActiveRequirementAction(requirementKey || type);
    try {
      const sessionToken = resolvePortalSessionToken(activeUuid);
      const formData = new FormData();
      formData.append('uuid', activeUuid);
      formData.append('type', type);
      formData.append('portalSessionToken', sessionToken);
      formData.append('file', file);
      if (requirementKey) {
        formData.append('requirementKey', requirementKey);
      }
      const endpoint = type === 'invoice' ? '/creative/portal/invoice/upload' : '/creative/portal/deliverable/upload';
      const data = await uploadRequest(endpoint, formData, portalSessionHeaders(activeUuid));
      setPortalData(data);
      notifySuccess(type === 'invoice' ? t('invoiceUploaded') : t('deliverableUploaded'));
    } catch (err) {
      if (handlePortalSessionError(activeUuid, err)) return;
      notifyError(t('uploadFailed', { message: err.message }));
    } finally {
      setActiveRequirementAction('');
      setActionLoading(false);
    }
  };

  const handleDeliverableValueSubmit = async (requirement) => {
    const requirementKey = requirement?.key;
    if (!activeUuid || !requirementKey) return;
    const rawValue = String(deliverableDrafts?.[requirementKey] || '').trim();
    const value = requirementKey === 'integration_timestamp'
      ? normalizeIntegrationTimestamp(rawValue)
      : rawValue;
    if (!value) {
      notifyError(t('enterValueFor', { label: requirement?.label }));
      return;
    }
    if (requirementKey === 'integration_timestamp' && !isValidIntegrationTimestamp(value)) {
      notifyError(t('timestampFormatError'));
      return;
    }

    resetMessages();
    setActionLoading(true);
    setActiveRequirementAction(requirementKey);
    try {
      const sessionToken = resolvePortalSessionToken(activeUuid);
      const formData = new FormData();
      formData.append('uuid', activeUuid);
      formData.append('type', 'deliverable');
      formData.append('portalSessionToken', sessionToken);
      formData.append('requirementKey', requirementKey);
      formData.append('value', value);
      const data = await uploadRequest('/creative/portal/deliverable/upload', formData, portalSessionHeaders(activeUuid));
      setPortalData(data);
      setDeliverableDrafts((prev) => ({ ...prev, [requirementKey]: '' }));
      notifySuccess(t('deliverableSaved', { label: requirement?.label }));
    } catch (err) {
      if (handlePortalSessionError(activeUuid, err)) return;
      notifyError(t('uploadFailed', { message: err.message }));
    } finally {
      setActiveRequirementAction('');
      setActionLoading(false);
    }
  };

  if (!portalData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          px: 2,
          py: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDark
            ? 'radial-gradient(circle at 10% 20%, #111426 0%, #090b14 42%, #04050a 100%)'
            : 'radial-gradient(circle at 10% 20%, #eaf2ff 0%, #f7faff 42%, #ffffff 100%)'
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 560,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            px: { xs: 3, sm: 5 },
            py: { xs: 3, sm: 4 },
            boxShadow: '0 24px 80px rgba(9, 30, 66, 0.12)'
          }}
        >
          <Stack spacing={2.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ width: 40 }} />
              <Box component="img" src={isDark ? jmgLogoLight : jmgLogoDark} alt="Joined Media" sx={{ width: 140, height: 'auto' }} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={t('language')}>
                  <IconButton onClick={cycleLanguage} aria-label="Cycle language">
                    <Typography variant="caption" fontWeight={700}>
                      {language}
                    </Typography>
                  </IconButton>
                </Tooltip>
                <Tooltip title={themeLabel}>
                  <IconButton onClick={cycleThemeMode} aria-label="Switch theme mode">
                    {themeIcon}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
              <Typography variant="h4" align="center">{t('title')}</Typography>
            {loginStep === 'error' ? (
              <>
                <Alert severity="error">{authErrorMessage || t('loginFailed', { message: '' })}</Alert>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('verificationFailedRestart')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleBackToLogin}
                  sx={{ py: 1.2 }}
                >
                  {t('backToLogin')}
                </Button>
              </>
            ) : loginStep === 'verify' ? (
              <>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('verifyCodeHint', { destination: challengeDestination })}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  onPaste={handleOtpPaste}
                >
                  {[0, 1, 2, 3, 4, 5].map((idx) => (
                    <TextField
                      key={idx}
                      value={otpDigits[idx]}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      inputRef={(el) => { otpRefs.current[idx] = el; }}
                      inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        style: { textAlign: 'center', fontSize: 22, width: 32, padding: '10px 6px' },
                      }}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleVerifyCode}
                    disabled={loading}
                    sx={{ py: 1.2, flex: 1 }}
                  >
                    {loading ? t('checkingCode') : t('verifyCode')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleLogin(loginUuid)}
                    disabled={loading}
                    sx={{ py: 1.2 }}
                  >
                    {t('resend')}
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" align="center">
                  {discordPendingLinkToken
                    ? 'Discord verified. Enter your My ID to receive a verification code and link your account.'
                    : t('enterMyIdHint')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('myId')}
                  value={loginUuid}
                  onChange={(e) => setLoginUuid(e.target.value)}
                  autoFocus
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleLogin()}
                  disabled={loading}
                  sx={{ py: 1.2 }}
                >
                  {loading ? t('sendingCode') : t('sendVerificationCode')}
                </Button>
                {!discordPendingLinkToken ? (
                  <>
                    <Divider>{'OR'}</Divider>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => handleDiscordAuth('login')}
                      disabled={loading || discordActionLoading}
                      sx={{ py: 1.2 }}
                      startIcon={discordActionLoading ? <CircularProgress size={14} color="inherit" /> : <DiscordLogoIcon fontSize="small" />}
                    >
                      {discordActionLoading ? t('openingDiscord') : t('loginWithDiscord')}
                    </Button>
                  </>
                ) : null}
              </>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : null}
          </Stack>
        </Paper>
        <Dialog open={emailPromptOpen} onClose={() => !loading && setEmailPromptOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t('missingEmailTitle')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('missingEmailDescription')}
              </Typography>
              <TextField
                fullWidth
                label={t('emailAddress')}
                value={emailPromptValue}
                onChange={(e) => setEmailPromptValue(e.target.value)}
                type="email"
                autoFocus
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailPromptOpen(false)} disabled={loading}>
              {t('backToLogin')}
            </Button>
            <Button onClick={handleSendFallbackEmail} variant="contained" disabled={loading}>
              {loading ? t('sendingCode') : t('send')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        px: 2,
        py: 2,
        background: isDark
          ? 'radial-gradient(circle at 12% 20%, #111426 0%, #090b14 42%, #04050a 100%)'
          : 'radial-gradient(circle at 12% 20%, #eaf2ff 0%, #f7faff 42%, #ffffff 100%)'
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h4">{t('title')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('myId')}: {portalData?.uuid || '-'}</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t('language')}>
                <IconButton onClick={handleOpenLanguageMenu} aria-label="Switch language">
                  <Typography variant="caption" fontWeight={700}>
                    {language}
                  </Typography>
                </IconButton>
              </Tooltip>
              <Tooltip title={themeLabel}>
                <IconButton onClick={cycleThemeMode} aria-label="Switch theme mode">
                  {themeIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title={portalUserName || t('accountMenu')}>
                <IconButton onClick={handleOpenUserMenu} aria-label={t('accountMenu')}>
                  <Avatar
                    src={portalUserAvatarSrc || ''}
                    alt={portalUserName || 'User'}
                    sx={{ width: 34, height: 34 }}
                  >
                    {portalUserInitial}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Box sx={{ flex: 1, overflowY: 'auto', pb: 2 }}>
          <Paper sx={{ p: 3 }}>
            {isActivationsPage ? (
              <Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h5">{t('myActivations')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('myActivationsDescription')}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleOpenActivationDetailPage}
                    disabled={!activeUuid}
                  >
                    {t('backToActivation')}
                  </Button>
                </Stack>

                {activationItems.length === 0 ? (
                  <Alert severity="info">{t('noActivations')}</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {activationItems.map((entry) => {
                      const itemUuid = String(entry?.uuid || '').trim();
                      const selected = itemUuid === String(activeUuid || '').trim();
                      const productImage = resolveActivationProductImage(entry);
                      const campaignName = entry?.campaign?.name || '-';
                      const productName = entry?.campaign?.product?.name || '';

                      return (
                        <Grid item xs={12} sm={6} md={4} key={itemUuid || `${campaignName}-${entry?.startDate || ''}`}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              borderRadius: 3,
                              borderWidth: selected ? 2 : 1,
                              borderColor: selected ? 'primary.main' : 'divider',
                              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                            }}
                          >
                            <CardActionArea
                              onClick={() => handleOpenActivationCard(itemUuid)}
                              disabled={activationSwitching || !itemUuid}
                              sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 160,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  overflow: 'hidden',
                                  bgcolor: productImage ? 'background.paper' : 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {productImage ? (
                                  <Box
                                    component="img"
                                    src={productImage}
                                    alt={productName || campaignName}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    {t('noActivationImage')}
                                  </Typography>
                                )}
                              </Box>

                              <CardContent sx={{ width: '100%' }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {campaignName}
                                  </Typography>
                                  {selected ? <Chip size="small" label={t('activeActivation')} color="primary" /> : null}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {entry?.channel?.name || '-'}
                                </Typography>
                                {Array.isArray(entry?.subCreatives) && entry.subCreatives.length > 0 ? (
                                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                    {entry.subCreatives.map((subCreative) => (
                                      <Chip
                                        key={subCreative.uuid}
                                        size="small"
                                        variant="outlined"
                                        label={`${subCreative.channel?.platform || 'content'} · ${subCreative.format || 'deliverable'}`}
                                      />
                                    ))}
                                  </Stack>
                                ) : null}
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                  {t('myEarnings')}: {formatActivationEarnings(entry, language)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {entry?.state || '-'}{entry?.startDate ? ` · ${formatCreatorPortalDateTime(entry.startDate, language)}` : ''}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            ) : !portalData ? <CircularProgress size={28} /> : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">{t('campaign')}</Typography>
                  <Typography variant="body1">{portalData?.campaign?.name || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">{t('channel')}</Typography>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      src={channelAvatarSrc || '/defaultavatar.png'}
                      alt={portalData?.channel?.name || 'Channel'}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Typography variant="body1">{portalData?.channel?.name || '-'}</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">{t('currentStatus')}</Typography>
                  <Chip label={portalData?.state || 'unknown'} color="primary" variant="outlined" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">{t('creative')}</Typography>
                  {portalData?.creativeId ? (
                    <Tooltip
                      placement="right-start"
                      arrow
                      enterDelay={200}
                      leaveDelay={120}
                      title={
                        resolveEmbedSrc(portalData?.type, portalData?.creativeId) ? (
                          <Box sx={{ width: 340, height: 220 }}>
                            <Box
                              component="iframe"
                              src={resolveEmbedSrc(portalData?.type, portalData?.creativeId)}
                              title="Creative preview"
                              sx={{ width: '100%', height: '100%', border: 0 }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </Box>
                        ) : (
                          <Typography variant="caption">{t('previewNotAvailableForType')}</Typography>
                        )
                      }
                      slotProps={{
                        tooltip: {
                          sx: {
                            p: 0,
                            maxWidth: 380,
                            bgcolor: 'transparent',
                            boxShadow: 'none'
                          }
                        },
                        popper: {
                          sx: { zIndex: 10000 }
                        }
                      }}
                    >
                      <Link
                        href={resolveCreativeHref(portalData?.type, portalData?.creativeId)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {getPublicCreativeId(portalData.creativeId)}
                      </Link>
                    </Tooltip>
                  ) : (
                    <Typography variant="body1">-</Typography>
                  )}
                </Grid>

                <Grid item xs={12} container spacing={2} alignItems="stretch">
                  <Grid item xs={12} md={8}>
                    <HR data-content={t('details')} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>{t('performance')}</Typography>
                      <Typography variant="body2">{t('latestViews')}: {portalData?.latestViews || 0}</Typography>
                      <Typography variant="body2">{t('estimatedViews')}: {portalData?.estimatedViews || 0}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2">{t('fillrate')}:</Typography>
                        {portalData?.fillRate === null ? (
                          <Typography variant="body2">-</Typography>
                        ) : (
                          <FillRing value={portalData.fillRate} />
                        )}
                      </Stack>

                      <Typography variant="h6" sx={{ mb: 1 }}>{t('contract')}</Typography>
                      {(portalData?.contractFiles || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">{t('noContract')}</Typography>
                      ) : (
                        <Stack spacing={1}>
                          <Link
                            href={portalData.contractFiles[0].url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => handleOpenAttachmentPreview(e, portalData.contractFiles[0])}
                          >
                            {portalData.contractFiles[0].name || t('contract')}
                          </Link>
                          {portalData.contractFiles.length > 1 ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('additionalVersions', { count: portalData.contractFiles.length - 1 })}
                            </Typography>
                          ) : null}
                        </Stack>
                      )}
                    </Box>

                    <HR/>

                    <Typography variant="h6" sx={{ mb: 1 }}>{t('deliverables')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t('deliverablesHint')}
                    </Typography>
                    <Stack spacing={1.5} sx={{ mt: 1, mb: 3 }}>
                      {requiredDeliverables.map((requirement) => {
                        const items = deliverablesByRequirement[requirement.key] || [];
                        const latestItem = requirement.latest || items[0] || null;
                        const isBusy = actionLoading && activeRequirementAction === requirement.key;

                        return (
                          <Paper key={requirement.key} variant="outlined" sx={{ p: 1.25 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                              <Box>
                                <Typography variant="subtitle2">{requirement.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{requirement.description}</Typography>
                              </Box>
                              <Chip
                                size="small"
                                color={requirement.completed ? 'success' : 'warning'}
                                label={requirement.completed ? t('delivered') : t('missing')}
                                sx={{
                                  borderRadius: '8px',
                                  width: 120,
                                  justifyContent: 'center',
                                  '& .MuiChip-label': {
                                    width: '100%',
                                    textAlign: 'center',
                                  }
                                }}
                              />
                            </Stack>

                            {(requirement.inputType === 'link' || requirement.inputType === 'text') ? (
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                                <Box sx={{ flex: 1, width: '100%' }}>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label={
                                      requirement.key === 'integration_timestamp'
                                      ? t('integrationTimestamp')
                                      : requirement.inputType === 'link'
                                        ? t('pasteUrl')
                                        : t('value')
                                  }
                                  placeholder={requirement.key === 'integration_timestamp' ? t('integrationTimestampPlaceholder') : ''}
                                  helperText={requirement.key === 'integration_timestamp' ? t('integrationTimestampHint') : ' '}
                                    value={deliverableDrafts?.[requirement.key] || ''}
                                    onChange={(e) => setDeliverableDrafts((prev) => ({ ...prev, [requirement.key]: e.target.value }))}
                                    onBlur={() => {
                                      if (requirement.key !== 'integration_timestamp') return;
                                      setDeliverableDrafts((prev) => ({
                                        ...prev,
                                        [requirement.key]: normalizeIntegrationTimestamp(prev?.[requirement.key] || '')
                                      }));
                                    }}
                                  />
                                </Box>
                                <Button
                                  variant="outlined"
                                  onClick={() => handleDeliverableValueSubmit(requirement)}
                                  disabled={isBusy}
                                  startIcon={isBusy ? <CircularProgress size={14} color="inherit" /> : null}
                                  sx={{
                                    width: 120,
                                    minWidth: 120,
                                    maxWidth: 120,
                                    height: 40,
                                    minHeight: 40,
                                  }}
                                >
                                  {isBusy ? t('saving') : t('save')}
                                </Button>
                              </Stack>
                            ) : null}

                            {requirement.inputType === 'file' ? (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  component="label"
                                  variant="outlined"
                                  color="primary"
                                  disabled={isBusy}
                                  startIcon={isBusy ? <CircularProgress size={14} color="inherit" /> : null}
                                  sx={{
                                    minWidth: 150,
                                    boxShadow: 'none',
                                    '&.Mui-disabled': {
                                      borderColor: 'action.disabledBackground',
                                      color: 'text.disabled',
                                    }
                                  }}
                                >
                                  {isBusy ? t('uploading') : t('uploadFile')}
                                  <input hidden type="file" onChange={(event) => handleFileUpload(event, 'deliverable', requirement.key)} />
                                </Button>
                              </Box>
                            ) : null}

                            {latestItem ? (
                              <Box sx={{ mt: 1 }}>
                                {latestItem.url ? (
                                  <Tooltip
                                    placement="right-start"
                                    arrow
                                    enterDelay={200}
                                    leaveDelay={120}
                                    title={<DeliverablePreview file={latestItem} text={i18n} />}
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          p: 0.5,
                                          maxWidth: 380,
                                          bgcolor: 'transparent',
                                          boxShadow: 'none'
                                        }
                                      },
                                      popper: {
                                        sx: { zIndex: 10000 }
                                      }
                                    }}
                                  >
                                    <Link
                                      href={latestItem.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => handleOpenAttachmentPreview(e, latestItem)}
                                    >
                                      {latestItem.name || latestItem.value || 'Deliverable'}
                                    </Link>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="body2">{latestItem.value}</Typography>
                                )}
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {formatCreatorPortalDateTime(latestItem.uploadedAt, language)}
                                </Typography>
                              </Box>
                            ) : null}
                          </Paper>
                        );
                      })}
                    </Stack>

                    < HR />

                    <Typography variant="h6" sx={{ mb: 1 }}>{t('invoice')}</Typography>
                    {portalData?.portal?.canUploadInvoice === false ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {t('missing')}: {' '}
                        {(portalData?.portal?.missingRequiredDeliverables || []).map((item) => item.label).join(', ')}
                      </Alert>
                    ) : null}
                    <Typography variant="body2">
                      {t('status')}: {portalData?.supplyInvoice?.status || '-'}
                    </Typography>
                    {portalData?.supplyInvoice?.acceptanceState ? (
                      <Box sx={{ mt: 1, mb: 1, maxWidth: 760 }}>
                        <SupplyInvoiceAcceptanceState
                          showButtons={false}
                          initial={portalData.supplyInvoice.acceptanceState}
                          oldSystem={false}
                          paid={portalData?.supplyInvoice?.status === 'paid'}
                          defaultView={true}
                          labelMode="creator"
                        />
                      </Box>
                    ) : null}
                    {portalData?.supplyInvoice?.number ? (
                      <Typography variant="body2">
                        {t('number')}: {portalData.supplyInvoice.number}
                      </Typography>
                    ) : null}
                    {portalData?.supplyInvoice?.file?.url ? (
                      <Box sx={{ mt: 0.5, mb: 1 }}>
                        <Link
                          href={portalData.supplyInvoice.file.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => handleOpenAttachmentPreview(e, portalData.supplyInvoice.file)}
                        >
                          {portalData.supplyInvoice.file.name || t('invoice')}
                        </Link>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('noInvoiceFile')}
                      </Typography>
                    )}
                    <Button
                      component="label"
                      variant="outlined"
                      disabled={actionLoading || portalData?.portal?.canUploadInvoice === false}
                      startIcon={actionLoading && activeRequirementAction === 'invoice' ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                      {actionLoading && activeRequirementAction === 'invoice' ? t('uploading') : t('uploadInvoice')}
                      <input hidden type="file" onChange={(event) => handleFileUpload(event, 'invoice')} />
                    </Button>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {(portalData?.supplyInvoice?.files || []).map((file) => (
                        <Box key={file.id}>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => handleOpenAttachmentPreview(e, file)}
                          >
                            {file.name}
                          </Link>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {formatCreatorPortalDateTime(file.uploadedAt || file.when, language)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <HR data-content={t('history')} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="h6">{t('communication')}</Typography>
                        <Tooltip title="Refresh messages">
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleRefreshMessages}
                              disabled={messagesRefreshing || commentSubmitting}
                              aria-label="Refresh messages"
                            >
                              {messagesRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                        <Stack direction={{ xs: 'column', md: 'column' }} spacing={1}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={t('writeMessage')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            disabled={commentSubmitting}
                          />
                          <Stack direction="row" spacing={1} alignItems="center">
                            {canEditLastCreatorMessage && !isEditingMessage ? (
                              <Button
                                variant="text"
                                onClick={handleStartEditLastMessage}
                                disabled={commentSubmitting}
                              >
                                Edit last sent message
                              </Button>
                            ) : null}
                            {isEditingMessage ? (
                              <Button
                                variant="text"
                                onClick={handleCancelEditLastMessage}
                                disabled={commentSubmitting}
                              >
                                Cancel edit
                              </Button>
                            ) : null}
                            <Tooltip title={messageBlockedTooltip}>
                              <span>
                                <Button variant="outlined" onClick={handleCommentSubmit} disabled={sendButtonDisabled}>
                                  {isEditingMessage ? 'Save edit' : t('send')}
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>

                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {portalMessages.slice().reverse().map((entry) => (
                            <Paper key={entry.id || entry.createdAt} variant="outlined" sx={{ p: 1.5 }}>
                              {entry?.author === 'account_manager' ? (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  {`JMG ${String(entry?.authorName || portalData?.accountManager || 'AM')}`}
                                </Typography>
                              ) : null}
                              <Typography variant="body2">{entry.message}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCreatorPortalDateTime(entry.createdAt, language)}
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    </Box>

                    < HR />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('activity')}
                    </Typography>
                    <Box sx={{ height: 260, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                      <Stack spacing={1}>
                        {activityGroups.map((group) => (
                          <Paper key={group.id} variant="outlined" sx={{ p: 1 }}>
                            <Typography variant="caption" display="block">
                              {group.what}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatCreatorPortalDateTime(group.items[0]?.when, language)}{group.items[0]?.who ? ` · ${group.items[0].who}` : ''}
                            </Typography>
                            {group.items.length > 1 ? (
                              <>
                                <Typography
                                  component="button"
                                  variant="caption"
                                  onClick={() => toggleActivityGroup(group.id)}
                                  sx={{
                                    mt: 0.25,
                                    p: 0,
                                    border: 0,
                                    background: 'none',
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {expandedActivityGroups[group.id]
                                    ? t('hideCount', { count: group.items.length })
                                    : t('showCount', { count: group.items.length })}
                                </Typography>
                                {expandedActivityGroups[group.id] ? (
                                  <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                                    {group.items.map((item, idx) => (
                                      <Typography key={`${group.id}-${idx}`} variant="caption" color="text.secondary" display="block">
                                        {formatCreatorPortalDateTime(item.when, language)}{item?.who ? ` · ${item.who}` : ''}
                                      </Typography>
                                    ))}
                                  </Box>
                                ) : null}
                              </>
                            ) : null}
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Box>
      </Box>

      {!!openAttachmentPreview && (
        <EditSlider
          open={!!openAttachmentPreview}
          onClose={() => setOpenAttachmentPreview(null)}
          title={openAttachmentPreview?.name || t('attachmentPreview')}
        >
          {(() => {
            const url = openAttachmentPreview?.url;
            const kind = getPreviewKindByUrl(url, openAttachmentPreview?.contentType);
            if (!url) return <Box sx={{ p: 2 }}><Typography>{t('noPreviewAvailable')}</Typography></Box>;

            if (kind === 'image') {
              return (
                <Box sx={{ p: 2 }}>
                  <Box
                    component="img"
                    src={resolveDirectMediaSrc(url)}
                    alt={openAttachmentPreview?.name || 'Attachment Preview'}
                    sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain' }}
                  />
                </Box>
              );
            }

            if (kind === 'video') {
              return (
                <Box sx={{ p: 2 }}>
                  <Box component="video" src={resolveDirectMediaSrc(url)} controls sx={{ width: '100%', maxHeight: '85vh' }} />
                </Box>
              );
            }

            if (kind === 'pdf') {
              return <PDFView url={url} />;
            }

            if (kind === 'drive') {
              return <DriveBestEffortPreview url={url} text={i18n} />;
            }

            const frameSrc = resolveDeliverableFrameSrc(url);

            return (
              <Box
                component="iframe"
                src={frameSrc}
                title={openAttachmentPreview?.name || t('attachmentPreview')}
                sx={{ width: '100%', minHeight: '85vh', border: 0 }}
              />
            );
          })()}
        </EditSlider>
      )}

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleCloseUserMenu}
      >
        {!isActivationsPage ? (
          <MenuItem onClick={handleOpenActivationsPage}>
            {t('myActivationsMenuLabel', { count: activationItems.length })}
          </MenuItem>
        ) : (
          <MenuItem onClick={handleOpenActivationDetailPage}>{t('backToActivation')}</MenuItem>
        )}
        {!isDiscordConnected ? (
          <MenuItem onClick={handleConnectDiscordFromMenu} disabled={discordActionLoading || !activeUuid}>
            {discordActionLoading ? t('openingDiscord') : t('connectDiscord')}
          </MenuItem>
        ) : null}
        {!isDiscordIdentitySession ? (
          <MenuItem onClick={handleBackToLoginFromMenu} disabled={logoutLoading}>
            {t('useAnotherId')}
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleOpenItFeedbackModal} disabled={itFeedbackSubmitting || logoutLoading}>
          Contact IT
        </MenuItem>
        <MenuItem onClick={handleLogoutFromMenu} disabled={logoutLoading}>
          {logoutLoading ? t('loggingOut') : t('logout')}
        </MenuItem>
      </Menu>

      <Dialog
        open={itFeedbackOpen}
        onClose={handleCloseItFeedbackModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Contact IT</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            Please use this message to report any bugs or improvement suggestions:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            value={itFeedbackMessage}
            onChange={(event) => setItFeedbackMessage(event.target.value)}
            disabled={itFeedbackSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseItFeedbackModal} disabled={itFeedbackSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitItFeedback}
            variant="contained"
            disabled={itFeedbackSubmitting || !String(itFeedbackMessage || '').trim()}
          >
            {itFeedbackSubmitting ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleCloseLanguageMenu}
      >
        {UI_LANGUAGE_OPTIONS.map((lang) => (
          <MenuItem
            key={lang}
            selected={language === lang}
            onClick={() => handleSelectLanguage(lang)}
          >
            {lang}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default CreatorPortal;
